import * as yup from 'yup';

import commonValidators from '../../core/validators/commonValidators';
import flightValidators from '../../core/validators/flightValidators';
import { SafetyEventReport, RadioGroupOption } from '../../core/api/clients';

type SafetyEventReportData = Pick<SafetyEventReport,
'flightNumber' |
'mainlinePartner' |
'aircraftNumber' |
'departureAirport' |
'scheduledArrivalAirport' |
'eventDate' |
'eventTitle' |
'locationOfEvent' |
'captain' |
'firstOfficer' |
'tsbNotificationName' |
'pressReleaseName' |
'internalAnnouncementName' |
'$discriminator'
>;
const SafetyEventFormValidationSchema: yup.ObjectSchema<SafetyEventReportData> = yup.object({
  // Flight & Crew
  flightNumber: flightValidators.flightNumber({ label: 'Flight Number', step: 0, optionalIfBlankGenerated: true }),
  aircraftNumber: flightValidators.aircraftNumber({ label: 'Aircraft Number', step: 0, required: true }),
  mainlinePartner: flightValidators.mainlinePartner({ label: 'Mainline Partner', step: 0, optionalIfBlankGenerated: true }),
  departureAirport: flightValidators.airport({ label: 'Departure Airport', step: 0, required: true }),
  scheduledArrivalAirport: flightValidators.airport({ label: 'Scheduled Arrival Airport', step: 0, required: true }),
  // landingAirport: flightValidators.airport({ label: 'Landing Airport', step: 0, required: true }),

  captain: flightValidators.crewMember({ label: 'Captain', step: 0, optionalIfBlankGenerated: true }),
  firstOfficer: flightValidators.crewMember({ label: 'First Officer', step: 0, optionalIfBlankGenerated: true }),

  // Event
  eventTitle: commonValidators.nullableString({ label: 'Event Title', step: 1, required: true, maxLength: 75 }),
  locationOfEvent: commonValidators.nullableString({ label: 'Location of Event', step: 1 }),
  eventDate: commonValidators.nullableString({ label: 'Event Date', required: true, step: 1 }),

  localEventTime: commonValidators.time({
    message: 'Local Event Time must be a valid 24-hour time',
    label: 'Local Event Time',
    required: true,
    step: 1,
  }),

  eventType: commonValidators.nullableString({
    label: 'Event Type',
    required: true,
    step: 1,
  }),

  eventType2: commonValidators.nullableString({
    label: 'Event Type 2',
    required: true,
    step: 1,
  }),

  eventType3: commonValidators.nullableString({
    label: 'Event Type 3',
    required: true,
    step: 1,
  }),

  typeOfFlight: commonValidators.nullableString({
    label: 'Type of Flight',
    required: true,
    step: 1,
  }),

  operationalImpact: commonValidators.nullableString({
    label: 'Operational Impact',
    required: true,
    step: 1,
  }),

  numberOfPassengers: yup.number()
    .label('Number of Passengers')
    .typeError('Number of Passengers must be a number')
    .integer()
    .nullable()
    .optional()
    .meta({ step: 1 }),

  flightDeckObserver: yup.string()
    .label('Flight Deck Observer')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  flightDeckObserverInformation: yup.string()
    .label('Flight Deck Observer Information')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  pilotFlying: yup.string()
    .label('Pilot Flying')
    .nullable()
    .optional()
    .meta({ step: 1 }),

  phaseOfFlight: yup.string()
    .label('Phase of Flight')
    .nullable()
    .required()
    .meta({ step: 1 }),

  phaseOfFlight2: yup.string()
    .label('Phase of Flight')
    .nullable()
    .required()
    .meta({ step: 1 }),

  // As Event Description
  narrative: yup.string()
    .label('Event Description')
    .nullable()
    .required()
    .meta({ step: 1 }),

  emergencyDeclared: commonValidators.radioGroupOption({ label: 'Emergency Declared', required: true, step: 1 }),
  passengersBriefed: commonValidators.radioGroupOption({ label: 'ARFF Responded', required: true, step: 1 }),
  cabinBraced: commonValidators.radioGroupOption({ label: 'Cabin Braced', required: true, step: 1 }),
  arffRequested: commonValidators.radioGroupOption({ label: 'ARFF Requested', required: true, step: 1 }),
  arffResponded: commonValidators.radioGroupOption({ label: 'ARFF Responded', required: true, step: 1 }),
  evacuation: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 1 }),
  emergencyEgressUtilized: commonValidators.radioGroupOption({ label: 'Emergency Egress Utilized', required: true, step: 1 }),
  crewInjury: commonValidators.radioGroupOption({ label: 'Crew Injury', required: true, step: 1 }),
  passengerInjury: commonValidators.radioGroupOption({ label: 'Passenger Injury', required: true, step: 1 }),
  groundPersonnelInjury: commonValidators.radioGroupOption({ label: 'Ground Personnel Injury', required: true, step: 1 }),
  aircraftDamage: commonValidators.radioGroupOption({ label: 'Aircraft Damage', required: true, step: 1 }),
  propertyDamage: commonValidators.radioGroupOption({ label: 'Property Damage', required: true, step: 1 }),
  adversePassengerReaction: commonValidators.radioGroupOption({ label: 'Adverse Passenger Reaction', required: true, step: 1 }),
  regulatoryComplianceConcern: commonValidators.radioGroupOption({ label: 'Regulatory Compliance Concern', required: true, step: 1 }),
  safetyComplianceConcern: commonValidators.radioGroupOption({ label: 'Safety Concern', required: true, step: 1 }),
  ferReminder: commonValidators.radioGroupOption({ label: 'FER Reminder', required: true, step: 1 }),
  cirpInfoOffered: commonValidators.radioGroupOption({ label: 'CIRP Info Offered', required: true, step: 1 }),

  // Crew Debrief
  // captainFitForDuty: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),
  // firstOfficerFitForDuty: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),
  // flightAttendantFitForDuty: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),
  // eventReviewRequired: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),
  // drugTestCrew: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),
  // crewReleasedToOperation: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),
  // aircraftReleasedToOperation: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),
  // fdrDownloadRemoval: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),
  // cvrDownload: commonValidators.radioGroupOption({ label: 'Evacuation', required: true, step: 2 }),

  // tsbNotification: commonValidators.radioGroupOption({ label: 'TSB Notification', required: true, step: 2 }),
  tsbNotificationName: flightValidators.crewMember({ label: 'TSB Notification Person',
    step: 2,
    requiredOnDependent: { fieldName: 'tsbNotification',
      value: RadioGroupOption.Yes } }),

  // ntsbNotification: commonValidators.radioGroupOption({ label: 'NTSB Notification', required: true, step: 2 }),

  // pressRelease: commonValidators.radioGroupOption({ label: 'Press Release', required: true, step: 2 }),
  pressReleaseName: flightValidators.crewMember({ label: 'Press Release Person',
    step: 2,
    requiredOnDependent: { fieldName: 'pressRelease',
      value: RadioGroupOption.Yes } }),

  // internalAnnouncement: commonValidators.radioGroupOption({ label: 'Internal Announcement', required: true, step: 2 }),
  internalAnnouncementName: flightValidators.crewMember({ label: 'Internal Announcement Person',
    step: 2,
    requiredOnDependent: { fieldName: 'internalAnnouncement',
      value: RadioGroupOption.Yes } }),
  $discriminator: yup.string().required().meta({ step: 0 }),
});

export default SafetyEventFormValidationSchema;
